import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal } from "./Reveal";
import { PORTFOLIO_TABS, PORTFOLIO_ITEMS, scrollToId } from "../data/site";

const Tabs = ({ tab, setTab }) => (
  <div className="flex flex-wrap gap-2">
    {PORTFOLIO_TABS.map((t) => (
      <button
        key={t.id}
        data-testid={t.testid}
        onClick={() => setTab(t.id)}
        className={`px-4 py-2 rounded-full text-xs font-sub font-semibold uppercase tracking-wider border transition-colors duration-200 ${
          tab === t.id
            ? "bg-gradient-to-r from-[#FF2A2A] to-[#FF6B00] text-white border-transparent"
            : "border-white/15 text-zinc-400 hover:border-[#FF6B00]/60 hover:text-white"
        }`}
      >
        {t.label}
      </button>
    ))}
  </div>
);

const Card = ({ item, i }) => (
  <motion.article
    layout
    data-testid={`portfolio-item-card-${i}`}
    initial={{ opacity: 0, scale: 0.94 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.94 }}
    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    className="group relative rounded-2xl overflow-hidden border border-white/10 bg-[#18181B]"
  >
    <div className="overflow-hidden">
      <img src={item.image} alt={`${item.title} — VIC WRAP STUDIO Melbourne`} loading="lazy" className="w-full h-64 sm:h-72 object-cover transition-transform duration-700 ease-out group-hover:scale-[1.08]" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
    <div className="absolute bottom-0 inset-x-0 p-5 translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
      <div className="font-tech text-[10px] uppercase tracking-[0.2em] text-[#FF6B00] mb-1.5">{item.cat} · {item.tag}</div>
      <h3 className="font-display text-xl font-bold uppercase tracking-wide leading-tight">{item.title}</h3>
    </div>
    <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10 group-hover:ring-[#FF6B00]/50 transition-shadow duration-300" />
  </motion.article>
);

const YourProjectCard = () => (
  <motion.button
    layout
    data-testid="portfolio-your-project-card"
    onClick={() => scrollToId("quote")}
    className="group rounded-2xl border-2 border-dashed border-white/15 hover:border-[#FF6B00]/60 min-h-64 sm:min-h-72 flex flex-col items-center justify-center gap-3 text-zinc-500 hover:text-[#FF6B00] transition-colors duration-300"
  >
    <span className="w-12 h-12 rounded-full border border-current flex items-center justify-center group-hover:rotate-90 transition-transform duration-300">
      <Plus size={20} />
    </span>
    <span className="font-display text-xl font-bold uppercase tracking-wide">Your project here</span>
    <span className="font-tech text-[10px] uppercase tracking-[0.2em]">Book your transformation</span>
  </motion.button>
);

export default function Portfolio() {
  const [tab, setTab] = useState("all");
  const filtered = tab === "all" ? PORTFOLIO_ITEMS : PORTFOLIO_ITEMS.filter((i) => i.cat === tab);

  return (
    <section id="portfolio" className="bg-[#0D0D0F] py-20 lg:py-28 scroll-mt-16 border-y border-white/5">
      <div className="container-x">
        <Reveal className="mb-12 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="eyebrow mb-4">PORTFOLIO</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95]">
              Work that <span className="text-gradient">turns heads</span>
            </h2>
          </div>
          <p className="text-zinc-500 text-sm max-w-sm leading-relaxed">
            A selection of recent wraps, film installs, signage and design work across Melbourne. Your project could be next.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mb-10">
          <Tabs tab={tab} setTab={setTab} />
        </Reveal>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((item, i) => <Card key={item.title} item={item} i={i} />)}
          </AnimatePresence>
          <YourProjectCard />
        </motion.div>
      </div>
    </section>
  );
}
